import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, STAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Rows3 } from "lucide-react";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import LabelPrintDialog from "@/components/LabelPrintDialog";

const EMPTY = {
  patient_name: "", patient_mrn: "", patient_dob: "", ordering_physician: "",
  specimen_type: "", specimen_site: "", origin_facility: "", specimen_class: "Surgical", cytology_type: "",
  priority: "Routine", pathologist_id: "", tech_id: "",
  gross_description: "", comments: "",
};

export default function Cases() {
  const { can } = useAuth();
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [lab, setLab] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", priority: "", pathologist_id: "", date_from: "", date_to: "", voided: "", synoptic: "" });
  const [compact, setCompact] = useState(false);
  const [open, setOpen] = useState(false);
  const [justAccessioned, setJustAccessioned] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cases", { params: filters });
      setCases(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(load, 5000);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/lab").then((r) => setLab(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.pathologist_id) delete payload.pathologist_id;
      if (!payload.tech_id) delete payload.tech_id;
      const { data } = await api.post("/cases", payload);
      toast.success(`Accessioned ${data.accession_number}`);
      setForm(EMPTY);
      setOpen(false);
      setJustAccessioned(data);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const pathologists = users.filter((u) => ["pathologist", "admin", "lab_director"].includes(u.role));
  const techs = users.filter((u) => ["tech", "supervisor", "admin"].includes(u.role));
  const rowPad = compact ? "py-1" : "py-2";

  return (
    <div className="space-y-5" data-testid="cases-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">PathTraX</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Case Tracking</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">{cases.length} case(s) matching filters. <LiveDot lastSync={lastSync} testid="cases-live-indicator" /></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCompact((v) => !v)} data-testid="toggle-density-button" className="h-11">
            <Rows3 size={15} className="mr-2" />{compact ? "Standard rows" : "Compact rows"}
          </Button>
          {can("cases:write") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]" data-testid="new-case-button">
                  <Plus size={16} className="mr-2" /> Accession case
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">New accession</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="new-case-form">
                  <Field label="Patient name" required>
                    <Input autoFocus required value={form.patient_name} onChange={set("patient_name")} data-testid="case-patient-name-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="MRN">
                    <Input value={form.patient_mrn} onChange={set("patient_mrn")} data-testid="case-mrn-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="Date of birth">
                    <Input value={form.patient_dob} onChange={set("patient_dob")} placeholder="YYYY-MM-DD" data-testid="case-dob-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="Ordering physician">
                    <Input value={form.ordering_physician} onChange={set("ordering_physician")} data-testid="case-physician-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="Specimen type">
                    <Input value={form.specimen_type} onChange={set("specimen_type")} data-testid="case-specimen-type-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="Specimen site">
                    <Input value={form.specimen_site} onChange={set("specimen_site")} data-testid="case-specimen-site-input" className="h-11 border-slate-300" />
                  </Field>
                  <Field label="Priority">
                    <select value={form.priority} onChange={set("priority")} data-testid="case-priority-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      <option value="Routine">Routine</option><option value="STAT">STAT</option>
                    </select>
                  </Field>
                  <Field label="Specimen class">
                    <select value={form.specimen_class} onChange={set("specimen_class")} data-testid="case-class-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      {(lab?.specimen_classes || ["Surgical", "Cytology"]).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Field>
                  {form.specimen_class === "Cytology" && (
                    <Field label="Cytology specimen type">
                      <input
                        list="cytology-types" value={form.cytology_type} onChange={set("cytology_type")}
                        data-testid="case-cytology-type-input" placeholder="e.g. Pap smear (ThinPrep)"
                        className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                      />
                      <datalist id="cytology-types">
                        {(lab?.cytology_types || []).map((k) => <option key={k} value={k} />)}
                      </datalist>
                    </Field>
                  )}
                  <Field label="Origin (where it came from)">
                    <input
                      list="origin-facilities" value={form.origin_facility} onChange={set("origin_facility")}
                      data-testid="case-origin-input" placeholder="e.g. GI Endoscopy Suite"
                      className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    />
                    <datalist id="origin-facilities">
                      {(lab?.origins || []).map((k) => <option key={k} value={k} />)}
                    </datalist>
                  </Field>
                  <Field label="Assigned pathologist">
                    <select value={form.pathologist_id} onChange={set("pathologist_id")} data-testid="case-pathologist-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      <option value="">Unassigned</option>
                      {pathologists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Assigned tech">
                    <select value={form.tech_id} onChange={set("tech_id")} data-testid="case-tech-select" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      <option value="">Unassigned</option>
                      {techs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Gross description (dictation-friendly)">
                      <Textarea value={form.gross_description} onChange={set("gross_description")} data-testid="case-gross-description-input" className="min-h-[150px] border-slate-300" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Comments">
                      <Textarea value={form.comments} onChange={set("comments")} data-testid="case-comments-input" className="min-h-[100px] border-slate-300" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="case-cancel-button" className="h-11">Cancel</Button>
                    <Button type="submit" disabled={busy} data-testid="case-submit-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                      {busy ? "Saving…" : "Create case"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid gap-2 border border-slate-200 bg-slate-50 p-3 md:grid-cols-3 lg:grid-cols-7" data-testid="case-filters">
        <div className="relative lg:col-span-2">
          <Search size={15} className="absolute left-3 top-3.5 text-slate-400" />
          <Input
            value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Accession, patient, MRN, physician…" data-testid="case-search-input"
            className="h-11 border-slate-300 bg-white pl-9"
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="filter-status-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">All statuses</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} data-testid="filter-priority-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any priority</option><option value="Routine">Routine</option><option value="STAT">STAT</option>
        </select>
        <select value={filters.pathologist_id} onChange={(e) => setFilters((f) => ({ ...f, pathologist_id: e.target.value }))} data-testid="filter-pathologist-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any pathologist</option>{pathologists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" className="h-11 border-slate-300 bg-white" />
        <select value={filters.synoptic} onChange={(e) => setFilters((f) => ({ ...f, synoptic: e.target.value }))} data-testid="filter-synoptic-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any synoptic state</option>
          <option value="incomplete">Synoptic incomplete</option>
          <option value="complete">Synoptic complete</option>
          <option value="none">No template applies</option>
        </select>
        <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))} data-testid="filter-voided-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Active cases</option>
          <option value="only">Voided only</option>
          <option value="include">Active + voided</option>
        </select>
      </div>

      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[900px] text-sm" data-testid="cases-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">Accession</th><th className="px-3 py-2">Patient</th><th className="px-3 py-2">MRN</th>
              <th className="px-3 py-2">Specimen</th><th className="px-3 py-2">Priority</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Pathologist</th><th className="px-3 py-2">Received</th><th className="px-3 py-2 text-right">TAT</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`case-row-${c.accession_number}`}>
                <td className={`px-3 ${rowPad}`}>
                  <Link to={`/cases/${c.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline" data-testid={`case-link-${c.accession_number}`}>
                    {c.accession_number}
                  </Link>
                </td>
                <td className={`px-3 ${rowPad} font-medium text-slate-800`}>
                  {c.patient_name}
                  {c.voided && <span className="ml-2 bg-red-800 px-1 py-0.5 text-[10px] font-bold text-white" data-testid={`voided-tag-${c.accession_number}`}>VOIDED</span>}
                </td>
                <td className={`px-3 ${rowPad} num text-slate-600`}>{c.patient_mrn || "—"}</td>
                <td className={`px-3 ${rowPad} text-slate-700`}>
                  {c.specimen_type || "—"}
                  {["incomplete", "not_started"].includes(c.synoptic_status) && !c.voided && (
                    <span title={c.synoptic_missing?.length ? `Missing: ${c.synoptic_missing.join(", ")}` : "No synoptic report started"}
                      data-testid={`synoptic-flag-${c.accession_number}`}
                      className="ml-2 whitespace-nowrap bg-amber-500 px-1 py-0.5 text-[10px] font-bold text-white">
                      SYNOPTIC {c.synoptic_status === "not_started" ? "NOT STARTED" : "INCOMPLETE"}
                    </span>
                  )}
                </td>
                <td className={`px-3 ${rowPad}`}>
                  {c.priority === "STAT"
                    ? <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white">STAT</span>
                    : <span className="text-slate-500">Routine</span>}
                </td>
                <td className={`px-3 ${rowPad}`}>
                  <span className="border border-[#134e4a]/30 bg-[#134e4a]/5 px-1.5 py-0.5 text-xs font-semibold text-[#134e4a]">{c.status}</span>
                </td>
                <td className={`px-3 ${rowPad} text-slate-700`}>{c.pathologist_name || "—"}</td>
                <td className={`px-3 ${rowPad} num text-slate-600`}>{fmtDateTime(c.received_at)}</td>
                <td className={`px-3 ${rowPad} num text-right font-semibold text-slate-800`}>{c.tat_hours ?? "—"}h</td>
              </tr>
            ))}
            {cases.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="cases-empty">No cases match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <LabelPrintDialog open={!!justAccessioned} onOpenChange={(o) => !o && setJustAccessioned(null)}
                        caseIds={justAccessioned ? [justAccessioned.id] : []}
                        caseId={justAccessioned?.id || ""} defaultSpec="container" extraSpec="cassette"
                        heading={justAccessioned
                          ? `Print labels for ${justAccessioned.accession_number}`
                          : "Print labels"} />
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
      {label}{required && <span className="text-red-800"> *</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);
